import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Building2, Check, CircleAlert, Mail } from 'lucide-react'
import { Alert, Button, Spinner } from '@agion/ui'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import { Field } from '@/components/common/Field'
import { authErrorMessage } from '@/services/auth-errors'
import { newPasswordHint } from '@/services/password-hint'
import { confirmWaitlistEmail, validateWaitlistToken } from '@/services/waitlistService'

/**
 * Set your password (design-of-record ui_kits/login/waitlist.html, email flow after the
 * verification link): locked verified email, organization name, password with strength,
 * confirmation. Confirm & join stays disabled until all fields are valid. auth-service
 * answers with a redirect (pending approval for an existing organization) or success.
 */
export const WaitlistCompletePage = () => {
  const { t } = useTranslation()
  const token = new URLSearchParams(window.location.search).get('token') || ''

  const [email, setEmail] = useState('')
  const [orgName, setOrgName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [isLoading, setIsLoading] = useState(Boolean(token))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [tokenError, setTokenError] = useState(() => (token ? '' : t('waitlist.invalidRequest')))

  useEffect(() => {
    if (!token) return
    const loadToken = async () => {
      try {
        const data = await validateWaitlistToken(token)
        setEmail(data.email)
        setOrgName(data.organization_hint || '')
      } catch (err) {
        setTokenError(authErrorMessage(err, t, 'waitlist.tokenExpired'))
      } finally {
        setIsLoading(false)
      }
    }
    loadToken()
  }, [token, t])

  const clearError = useCallback(() => setError(''), [])
  const handleOrgChange = (e: ChangeEvent<HTMLInputElement>) => { setOrgName(e.target.value); clearError() }
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => { setPassword(e.target.value); clearError() }
  const handleConfirmChange = (e: ChangeEvent<HTMLInputElement>) => { setConfirmPassword(e.target.value); clearError() }

  const orgValid = orgName.trim().length >= 2
  // The hint states the server's password rule; the server judges the password
  // and its refusal sentence is what the page shows (agion/Agion#3140, B4).
  const passwordValid = !!password
  const confirmMatches = !!confirmPassword && confirmPassword === password
  const canSubmit = orgValid && passwordValid && confirmMatches

  const passwordStrength = password.length >= 16
    ? t('waitlist.passwordStrong')
    : password.length >= 12
      ? t('waitlist.passwordGood')
      : t('waitlist.passwordWeak')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setPasswordTouched(true)
    if (!canSubmit) return

    setIsSubmitting(true)
    try {
      const resp = await confirmWaitlistEmail(token, orgName.trim(), password)
      if (resp.redirect_url) {
        window.location.href = resp.redirect_url
      } else {
        setIsSuccess(true)
      }
    } catch (err) {
      setError(authErrorMessage(err, t, 'waitlist.genericError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <AuthSplitLayout>
        <div className="lp-state" role="status" aria-label={t('waitlist.loading')}>
          <Spinner size="lg" />
        </div>
      </AuthSplitLayout>
    )
  }

  if (tokenError) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop">
          <div className="lp-badge lp-badge-brand">
            <CircleAlert size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('waitlist.linkExpiredTitle')}</h2>
          <p className="lp-state-msg">{tokenError}</p>
          <Link to="/waitlist" className="lp-back">
            {t('waitlist.backToWaitlist')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </AuthSplitLayout>
    )
  }

  if (isSuccess) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop">
          <div className="lp-badge lp-badge-success">
            <Check size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('waitlist.onListTitle')}</h2>
          <p className="lp-state-msg">{t('waitlist.onListMessage')}</p>
          <p className="lp-note">{t('waitlist.onListInbox')}</p>
        </div>
      </AuthSplitLayout>
    )
  }

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('waitlist.setPasswordTitle')}</h2>
        <p className="lp-subheading">{t('waitlist.setPasswordSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="lp-form lp-rise lp-rise-3">
        {error && <Alert variant="error">{error}</Alert>}

        <Field
          id="email"
          type="email"
          label={t('waitlist.workEmailLabel')}
          value={email}
          locked
          leadingIcon={<Mail size={16} />}
          trailing={
            <span className="lp-verified">
              <Check size={14} strokeWidth={2.5} aria-hidden="true" />
              {t('waitlist.verifiedMarker')}
            </span>
          }
        />

        <Field
          id="organization"
          type="text"
          label={t('waitlist.orgLabel')}
          value={orgName}
          onChange={handleOrgChange}
          valid={orgValid}
          leadingIcon={<Building2 size={16} />}
          placeholder={t('waitlist.orgPlaceholder')}
          autoComplete="organization"
          disabled={isSubmitting}
        />

        <Field
          id="password"
          type={showPassword ? 'text' : 'password'}
          label={t('waitlist.passwordLabel')}
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => setPasswordTouched(true)}
          valid={passwordTouched && passwordValid}
          successMessage={passwordStrength}
          hint={newPasswordHint(t)}
          placeholder={t('waitlist.passwordPlaceholder')}
          autoComplete="new-password"
          disabled={isSubmitting}
          labelAction={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="lp-field-action"
              aria-controls="password confirmPassword"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? t('waitlist.hidePassword') : t('waitlist.showPassword')}
            </Button>
          }
        />

        <Field
          id="confirmPassword"
          type={showPassword ? 'text' : 'password'}
          label={t('waitlist.confirmPasswordLabel')}
          value={confirmPassword}
          onChange={handleConfirmChange}
          error={confirmPassword && confirmPassword !== password ? t('waitlist.passwordsDontMatch') : ''}
          valid={confirmMatches && passwordValid}
          successMessage={t('waitlist.passwordsMatch')}
          placeholder={t('waitlist.confirmPasswordPlaceholder')}
          autoComplete="new-password"
          disabled={isSubmitting}
        />

        <Button
          type="submit"
          className="lp-btn-primary w-full"
          disabled={isSubmitting || !canSubmit}
          isLoading={isSubmitting}
        >
          {isSubmitting ? t('waitlist.submitting') : t('waitlist.confirmJoinButton')}
          {!isSubmitting && <ArrowRight size={16} aria-hidden="true" />}
        </Button>
      </form>
    </AuthSplitLayout>
  )
}
